import type { Ref } from 'vue';
import { useRoute } from 'vue-router';
import type { ContextType, ExtendedPartTagType } from '@/entities/rubrics';
import { getUrl } from '@/entities/rubrics';
import { getPathWithoutTag } from '@/entities/filter/store/utils/removeTagFromPath';
import { useTourFiltersStore } from '@/entities/filter/store/tour';
import type { RegionTag } from '@/shared/api/experience';
import { DEFAULT_RUBRIC } from '@/widgets/rubricsBlock/tour';
import { useTagRedirect } from '@/shared/lib/redirects';
export function useTourRegionSelectedTag(regionTags: Ref<RegionTag[] | undefined>, context: Ref<ContextType>) {
  const filterStore = useTourFiltersStore();
  const route = useRoute();

  const selectedTagFromRoute = computed(() => {
    const slug = filterStore.searchState.tag__slugs;

    if (!slug || slug === 'all') {
      return { ...DEFAULT_RUBRIC };
    }

    const foundTag = regionTags.value?.find((tag) => tag.slug === slug);
    if (!foundTag) {
      return { ...DEFAULT_RUBRIC };
    }

    return {
      value: foundTag.slug || '',
      label: foundTag.name || '',
      id: foundTag.id || undefined,
      counter: foundTag.counters?.[`${context.value}_count`] || undefined,
      url: getUrl(foundTag, context.value),
      seoUrl: foundTag.urls?.tours || '',
      is_tours_included: false,
    };
  });

  const tagForRedirect = computed(() => {
    if (filterStore.searchState.tag__slugs !== 'all') {
      return {};
    }
    const pathWithoutAll = getPathWithoutTag(route.path, 'all');
    if (pathWithoutAll === route.path) {
      return {};
    }

    const redirectUrl = `${window.location.origin}${pathWithoutAll}`;

    return { value: 'all' as const, redirect: redirectUrl };
  });
  useTagRedirect(tagForRedirect);

  const selectedValue = ref<ExtendedPartTagType>({ ...DEFAULT_RUBRIC });
  const cachedSelectedValue = ref<ExtendedPartTagType>({ ...DEFAULT_RUBRIC });

  watch(
    () => selectedTagFromRoute.value,
    (newSelectedTagFromRoute) => {
      if (newSelectedTagFromRoute.value) {
        selectedValue.value = newSelectedTagFromRoute;
        cachedSelectedValue.value = newSelectedTagFromRoute;
      }
    },
    { immediate: true },
  );

  return { selectedValue, cachedSelectedValue };
}
